import { useMemo } from "react";
import type { AppUser } from "@/lib/types/database";
import type { AssigneeAggregate, TaskRow } from "./types";
import { collectAssigneeIds } from "./utils";

/**
 * Aggregate ticket tasks by assignee, returning an alphabetically ordered list
 * of `{ userId, user, tasks }`. The user record is hydrated from the directory
 * (`allUsers`); if no match is found, `user` is `null` and the row shows the
 * raw ID as a fallback.
 */
export function useAssigneeAggregates(
  tasks: TaskRow[],
  allUsers: AppUser[],
): AssigneeAggregate[] {
  return useMemo(() => {
    const byUser = new Map<
      string,
      { userId: string; tasks: AssigneeAggregate["tasks"] }
    >();

    for (const task of tasks) {
      const ids = collectAssigneeIds(task);
      for (const uid of ids) {
        let entry = byUser.get(uid);
        if (!entry) {
          entry = { userId: uid, tasks: [] };
          byUser.set(uid, entry);
        }
        entry.tasks.push({
          id: task.id,
          title: task.title,
          status: task.status,
        });
      }
    }

    const list: AssigneeAggregate[] = Array.from(byUser.values()).map((entry) => {
      const user = allUsers.find((u) => u.id === entry.userId) ?? null;
      return { ...entry, user };
    });

    list.sort((a, b) => {
      const an = a.user
        ? `${a.user.first_name || ""} ${a.user.last_name || ""}`.trim() || a.user.email
        : a.userId;
      const bn = b.user
        ? `${b.user.first_name || ""} ${b.user.last_name || ""}`.trim() || b.user.email
        : b.userId;
      return an.localeCompare(bn);
    });

    return list;
  }, [tasks, allUsers]);
}
